// MentionTextarea — Phase 2c of the revised Pamosi Clinic EMR (2026-10-03).
//
// A plain <textarea> with @-mention autocomplete. Lives in the SharedInstructions
// panel so staff can tag teammates in notes; the matching server flow in
// apps/api/src/modules/db/mentions.ts resolves @usernames at write time.
//
// Behaviour:
//   - Typing `@` opens a popover with the clinic's member roster.
//   - Prefix filter runs as the user keeps typing (case-insensitive).
//   - Arrow keys move the selection; Enter/Tab accepts the highlighted
//     candidate and replaces the token (`@bo|` → `@bola `).
//   - Clicking a row accepts.
//   - Escape (or caret leaving the token) closes the popover.
//
// The popover is intentionally dumb — it mirrors the API's parsing rules
// (`@[A-Za-z0-9_]{3,32}`) so what you insert here is what the server will
// successfully resolve. Resolution still happens on write; typos just drop.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { MentionCandidate } from '@pamosi/shared';

interface Props {
    value: string;
    onChange: (next: string) => void;
    candidates: MentionCandidate[];
    placeholder?: string;
    minHeightClass?: string;
    maxLength?: number;
    disabled?: boolean;
}

const USERNAME_CHAR_RE = /[A-Za-z0-9_]/;

export function MentionTextarea(props: Props) {
    const {
        value,
        onChange,
        candidates,
        placeholder,
        minHeightClass = 'min-h-[64px]',
        maxLength,
        disabled = false,
    } = props;

    const taRef = useRef<HTMLTextAreaElement>(null);
    const [caret, setCaret] = useState(0);
    const [highlight, setHighlight] = useState(0);

    // Detect the active @token the caret sits inside. Walk back from caret
    // until whitespace/non-token char; if we hit a `@`, that's the token
    // start. Returns null if the caret isn't on an open token.
    const active = useMemo(() => {
        if (caret < 1) return null;
        const left = value.slice(0, caret);
        let i = left.length - 1;
        while (i >= 0 && USERNAME_CHAR_RE.test(left[i]!)) i -= 1;
        if (i < 0 || left[i] !== '@') return null;
        // Boundary — @ must follow start-of-string or a non-token char so we
        // don't trigger on "email@domain".
        if (i > 0 && USERNAME_CHAR_RE.test(left[i - 1]!)) return null;
        const query = left.slice(i + 1).toLowerCase();
        // Only usernames up to 32 chars are valid; collapse the popover once
        // the user has typed past that so we're not showing stale matches.
        if (query.length > 32) return null;
        return { start: i, query };
    }, [value, caret]);

    const filtered = useMemo(() => {
        if (!active) return [];
        const q = active.query;
        const rows = candidates.filter((c) => {
            if (!c.username) return false;
            if (q === '') return true;
            return c.username.toLowerCase().startsWith(q);
        });
        return rows.slice(0, 8);
    }, [active, candidates]);

    // Reset highlight when the candidate list changes so we don't point past
    // the end of a shrinking list.
    useEffect(() => { setHighlight(0); }, [active?.query, filtered.length]);

    const insert = (c: MentionCandidate) => {
        if (!active || !c.username) return;
        const before = value.slice(0, active.start);
        const after = value.slice(caret);
        const inserted = `@${c.username} `;
        const next = before + inserted + after;
        const nextCaret = (before + inserted).length;
        onChange(next);
        // Defer so React re-renders with the new value before we move the
        // caret, otherwise selectionStart lands on the stale text.
        requestAnimationFrame(() => {
            const el = taRef.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(nextCaret, nextCaret);
            setCaret(nextCaret);
        });
    };

    const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!active || filtered.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            // Enter inside a textarea normally inserts a newline; swallow it
            // only when a mention is being accepted.
            e.preventDefault();
            const pick = filtered[highlight];
            if (pick) insert(pick);
        } else if (e.key === 'Escape') {
            // Close the popover by clearing the active token. Caret moves
            // just past the current token; the user can still edit it.
            e.preventDefault();
            const el = taRef.current;
            if (!el) return;
            el.setSelectionRange(caret + 1, caret + 1);
        }
    };

    const popoverOpen = !!active && filtered.length > 0;

    return (
        <div className="relative">
            <textarea
                ref={taRef}
                className={`pm-input ${minHeightClass}`}
                placeholder={placeholder}
                value={value}
                maxLength={maxLength}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                    setCaret(e.target.selectionStart ?? e.target.value.length);
                }}
                onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
                onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
                onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
                onKeyDown={onKey}
            />
            {popoverOpen && (
                <div
                    role="listbox"
                    className="absolute z-10 mt-1 left-0 right-0 max-w-xs bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden"
                >
                    {filtered.map((c, idx) => (
                        <button
                            key={c.clinician_id}
                            type="button"
                            role="option"
                            aria-selected={idx === highlight}
                            // Mouse-down fires before the textarea's blur so
                            // insert() can still read the caret position.
                            onMouseDown={(e) => { e.preventDefault(); insert(c); }}
                            onMouseEnter={() => setHighlight(idx)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                                idx === highlight ? 'bg-brand-50' : 'bg-white hover:bg-slate-50'
                            }`}
                        >
                            <span className="text-slate-800">
                                <span className="font-medium">@{c.username}</span>
                                {c.full_name && (
                                    <span className="text-slate-500 ml-1.5">· {c.full_name}</span>
                                )}
                            </span>
                            <span className="text-slate-400 text-xs">{c.role}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
